import { Add } from "@mui/icons-material";
import { Box, Button, Snackbar, Typography } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useConfirm } from "material-ui-confirm";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	closeEditWorkerCertificationDialog,
	resetAddWorkerCertifications,
	resetDeleteWorkerCertifications,
	resetEditWorkerCertification,
	resetPostDeleteFiles,
	resetPostUploadFiles,
} from "../../../../../export/workers";
import SectionSearch from "../../../../design/section-search";
import { hasPermission } from "../../../../utils/helpers";
import useWorkersData from "../../controllers/use-workers-data";
import useWorkerCertificationsController from "./controllers/use-worker-certifications-controller";
import AddWorkerCertificationDialog from "./ui/dialog/add-worker-certifications";
import EditWorkerCertificationDialog from "./ui/dialog/edit-worker-certification";
import WorkerCertificationsTable from "./ui/table/worker-certifications-table";

const WorkerCertifications = () => {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const { t } = useTranslation();
	const {
		isEditWorkerCertificationDialogOpen,
		editWorkerCertificationStatus,
		postDeleteFilesStatus,
		postUploadFilesStatus,
		failedFileDeleteCount,
		failedFileUploadCount,
		workerProfile,
    userRolesWithPermission,
	} = useWorkersData();

	const { userWorkCenters } = useSelector(
		(state: any) => ({
			userWorkCenters:
				state.manageWorkers.filterData.allWorkcenters?.map((wc: any) => String(wc.id)) ||
				[],
		})
	);

	const isInUserWorkcenter = useMemo(() => {
		const workerWorkCenterId = workerProfile?.workCenter?.id;
		if (!workerWorkCenterId) return true;
		return userWorkCenters.includes(String(workerWorkCenterId));
	}, [workerProfile?.workCenter?.id, userWorkCenters]);

	const handleIsInUserWorkcenterAlert = useCallback(() => {
		confirm({
			title: "",
			description: t("workers_not-in-workcenter-alert"),
			confirmationText: t("common_close"),
			cancellationText: "",
		});
	}, [confirm, t]);
	const [
		{
			isAddCertificationDialogOpen,
			deleteCertificationError,
			deleteCertificationStatus,
			addWorkerCertificationError,
			addWorkerCertificationStatus,
			searchText,
		},
		{ openAddCertificationDialog, closeAddCertificationDialog, setSearchText },
	] = useWorkerCertificationsController();

	// Check if any operation has failed
	const isAnyOperationError =
		editWorkerCertificationStatus === "error" ||
		postDeleteFilesStatus === "error" ||
		postUploadFilesStatus === "error";

	// Check if any operation is successful
	const isAnyOperationFulfilled =
		editWorkerCertificationStatus === "fulfilled" ||
		postDeleteFilesStatus === "fulfilled" ||
		postUploadFilesStatus === "fulfilled";

	// Check if any operation is pending
	const isAnyOperationPending =
		editWorkerCertificationStatus === "pending" ||
		postDeleteFilesStatus === "pending" ||
		postUploadFilesStatus === "pending";

	// Helper function to close all Snackbars and reset statuses
	const handleCloseSnackbars = () => {
		dispatch(resetEditWorkerCertification());
		dispatch(resetPostDeleteFiles());
		dispatch(resetPostUploadFiles());
	};

	if (!isAnyOperationPending) {
		if (isAnyOperationFulfilled && !isAnyOperationError) {
			dispatch(closeEditWorkerCertificationDialog());
		}
	}
	return (
		<Box
			sx={{
				borderRadius: "10px",
				border: "3px solid #f4f4f4",
			}}
			mb={5}
			mt={5}
		>
			<Box p={2}>
				<Box>
					<Typography fontWeight='bold'>
					{t("workers_qualified-certifications")}
					</Typography>
					<Typography variant='caption'>
						{/* Add certifications that the worker has and dates they are valid. */}
					</Typography>
				</Box>
				<Box
					display={"flex"}
					alignItems={"center"}
					justifyContent={"space-between"}
					mt={2}
				>
					<SectionSearch
						placeholder={t("common_search")}
						value={searchText ? searchText : ""}
						setSearch={setSearchText}
					/>
					<Box>
            {hasPermission(userRolesWithPermission, "SETTINGS.EDIT_WORKERS") ? (
              <Button
                startIcon={<Add />}
                onClick={() => {
                  if (isInUserWorkcenter) {
                    openAddCertificationDialog();
                  } else {
                    handleIsInUserWorkcenterAlert();
                  }
                }}
                sx={{ opacity: isInUserWorkcenter ? 1 : 0.5 }}
              >
                {t("workers_certifications")}
              </Button>
            ) : <Box />}
					</Box>
				</Box>
			</Box>

			<Box
				sx={{
					padding: "12px 8px",
				}}
			>
				<WorkerCertificationsTable
					workerProfile={workerProfile}
					searchText={searchText ? searchText : ""}
				/>
			</Box>
			{isAddCertificationDialogOpen && (
				<AddWorkerCertificationDialog
					isOpen={isAddCertificationDialogOpen}
					onClose={closeAddCertificationDialog}
				/>
			)}

			{isEditWorkerCertificationDialogOpen && <EditWorkerCertificationDialog />}
			<Snackbar
				open={addWorkerCertificationStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetAddWorkerCertifications())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					{t("workers_certification-added-successfully")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={addWorkerCertificationStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetAddWorkerCertifications())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{addWorkerCertificationError ?? t("common_error-occurred")}
				</MuiAlert>
			</Snackbar>

			{/* Delete Certification for worker */}
			<Snackbar
				open={deleteCertificationStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteWorkerCertifications())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					{t("workers_certification-removed-successfully")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={deleteCertificationStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteWorkerCertifications())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{deleteCertificationError ?? t("common_error-occurred")}
				</MuiAlert>
			</Snackbar>

			{/* Edit Certification for worker */}
			{!isAnyOperationPending && (
				<>
					{/* Success Snackbar */}
					<Snackbar
						open={!isAnyOperationError && isAnyOperationFulfilled}
						autoHideDuration={3000}
						onClose={() => {
							handleCloseSnackbars();
						}}
						anchorOrigin={{ vertical: "top", horizontal: "center" }}
					>
						<MuiAlert elevation={6} variant='filled' severity='success'>
							{t("workers_certification-edited-successfully")}
						</MuiAlert>
					</Snackbar>

					{/* General Error Snackbar */}
					<Snackbar
						open={isAnyOperationError}
						autoHideDuration={3000}
						onClose={handleCloseSnackbars}
						anchorOrigin={{ vertical: "top", horizontal: "center" }}
					>
						<MuiAlert elevation={6} variant='filled' severity='error'>
							{editWorkerCertificationStatus === "error"
								? deleteCertificationError ?? t("common_error-occurred")
								: postDeleteFilesStatus === "error"
								? t("workers_failed-to-delete-files", { count: failedFileDeleteCount })
								: t("workers_failed-to-upload-files", { count: failedFileUploadCount })}
						</MuiAlert>
					</Snackbar>
				</>
			)}
		</Box>
	);
};

export default WorkerCertifications;
